import { Flex } from "rebass";
import {
  display,
  position,
  left,
  right,
  height,
  width,
  maxWidth,
  themeGet
} from "styled-system";
import styled, { css } from "styled-components";
import { theme } from "../styles/theme";
import SVG from "react-inlinesvg";
import { LazyImage } from "../components/lazy-image";
import { Title } from "./title";
import { useRef } from "react";
import { useWindowWidth } from "../utils/window-width";
import { useOnScreen } from "../utils/on-screen";

const TextImage = ({
  alternate,
  background_image,
  description,
  label,
  title
}) => {
  const alternateBool = alternate === "yes";
  const ref = useRef();
  const animBreakPoint = parseInt(theme.breakpoints[2], 10) * 16;
  let onScreen;
  let w;
  let onDesktop;
  onScreen = useOnScreen(ref, "-300px");
  w = useWindowWidth();
  onDesktop = w >= animBreakPoint;

  return (
    <Container
      position="relative"
      flexDirection="column"
      ref={ref}
      alternate={alternateBool}
      visible={onScreen}
      showAnimation={onDesktop}
    >
      <BgImage
        src={background_image.url}
        alt={background_image.alt}
        width={1}
        height={["300px", "350px", "400px", "900px"]}
      />

      <ContainerCentered
        width={1}
        maxWidth={3}
        flexDirection={alternateBool ? "row" : "row-reverse"}
        alignItems="center"
        position={["static", "static", "static", "absolute"]}
      >
        <OctagonWrap
          className="octagon-wrap"
          width={[1, 1, 1, 1 / 2]}
          px={[0, 0, 0, 5]} // Desktop/Octagon
          py={[0, 4, 5, 0]} // Mobile & Tablet/Full Width
          justifyContent="center"
          position="inherit"
          left={alternateBool ? theme.space[3] : "initial"}
          right={alternateBool ? "initial" : theme.space[3]}
          color={alternateBool ? "white" : "blue.1"}
          bg={
            alternateBool
              ? ["blue.1", "blue.1", "blue.1", "transparent"]
              : ["white", "white", "white", "transparent"]
          }
        >
          <S_Title label={label} title={title} description={description} />
          <Octagon
            src="/static/images/octagon.svg"
            alternateBool={alternateBool}
            width={1}
            display={["none", "none", "none", "block"]}
          />
        </OctagonWrap>
      </ContainerCentered>
    </Container>
  );
};

const ease = "cubic-bezier(0.465, 0.183, 0.153, 0.946)";

const Container = styled(Flex)`
  ${position}

  html.intersectionobserver & {
    ${props =>
      props.showAnimation &&
      !props.visible &&
      css`
        ${OctagonWrap} {
          transform: translate(${props.alternate ? "50px" : "-50px"}, 0);
          opacity: 0;
        }

        ${S_Title} {
          h2 {
            transform: translate(${props.alternate ? "15px" : "-15px"}, 0);
            opacity: 0;
          }

          div {
            transform: translate(${props.alternate ? "15px" : "-15px"}, 0);
            opacity: 0;
          }
        }
      `}
  }
`;

const S_Title = styled(Title)`
  h2 {
    transition: all 0.3s 0.2s ${ease};
    @media screen and (min-width: ${themeGet("breakpoints.1")}) {
      padding: 0 20px;
    }
  }

  div {
    transition: all 0.3s 0.3s ${ease};
  }
`;

const BgImage = styled(LazyImage)`
  ${height}
  object-fit: cover;
`;

const ContainerCentered = styled(Container)`
  ${maxWidth}
  height: 100%;

  @media screen and (min-width: ${theme.breakpoints[3]}) {
    left: 50%;
    transform: translateX(-50%);
  }
`;

const OctagonWrap = styled(Container)`
  ${left}
  ${right}
  transition: all 0.7s ${ease};

  & > *:not(:last-child) {
    z-index: 1;
  }
`;

const Octagon = styled(SVG)`
  ${width}
  ${display}
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.9;

  svg {
    height: 100%;
    width: 100%;
    fill: ${props =>
      props.alternateBool
        ? themeGet("colors.blue.1")
        : themeGet("colors.white")};
  }
`;

export { TextImage };
